/* eslint-disable react/prop-types */
import React, {
  useMemo,
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";
import Highcharts from "highcharts/highstock";
import * as HighchartsMoreModule from "highcharts/highcharts-more";
import HighchartsReact from "highcharts-react-official";
import * as HighchartsHeatMapModule from "highcharts/modules/heatmap";
import { find, merge } from "lodash";

import { ChartSettings, RoseChart, RoseModule } from "@types";
import { composeChartOptions } from "@utils/helpers/display/nontimeseries";
import { setHighchartsTheme } from "@utils/helpers/display/highchartsTheme";
import { useTheme } from "@theme";
import { ViewMode } from "@components/common";
import { ChartEditor } from "./ChartEditor";
import UserContainer from "@utils/state/userContainer";

// Handle ESM/CJS module interop for Highcharts modules
const initHighchartsMore =
  (HighchartsMoreModule as any).default || HighchartsMoreModule;
const initHighchartsHeatMap =
  (HighchartsHeatMapModule as any).default || HighchartsHeatMapModule;

if (typeof initHighchartsMore === "function") {
  initHighchartsMore(Highcharts);
}
if (typeof initHighchartsHeatMap === "function") {
  initHighchartsHeatMap(Highcharts);
}

// Set Highcharts global options once, outside component lifecycle
// This prevents repeated calls on every render
Highcharts.setOptions({
  lang: {
    numericSymbols: ["k", "m", "b", "t", "p", "e"],
  },
});

export type ChartProps = {
  roseObject: RoseChart;
  chartSettings: ChartSettings;
  onChartSettingsChanged: (value: ChartSettings) => void;
  mod: RoseModule;
  view: ViewMode;
};

export function Chart(props: ChartProps): JSX.Element | null {
  const { roseObject, chartSettings, onChartSettingsChanged, mod, view } =
    props;

  const theme = useTheme();
  const { user } = UserContainer.useContainer();
  const chartSettingsRef = useRef(chartSettings);
  chartSettingsRef.current = chartSettings;
  const getChartSettings = () => chartSettingsRef.current;

  // Guard against undefined chartSettings
  const [highchartOptions, setHighchartOptions] =
    useState<Highcharts.Options | null>(() => {
      if (!chartSettings) return null;
      return composeChartOptions({
        roseObject,
        theme,
        chartSettings,
        getChartSettings,
        onChartSettingsChanged,
        modSettings: mod?.modulesettings,
        isViewMode: view === "view",
        user,
      });
    });

  // Initialize highchartOptions when chartSettings becomes available
  useEffect(() => {
    if (chartSettings && !highchartOptions) {
      setHighchartOptions(
        composeChartOptions({
          roseObject,
          theme,
          chartSettings,
          getChartSettings,
          onChartSettingsChanged,
          modSettings: mod?.modulesettings,
          isViewMode: view === "view",
          user,
        }),
      );
    }
  }, [chartSettings]);

  // Memoize width factor calculation to avoid recalculating on every render
  const widthFactor = useMemo(() => {
    switch (mod?.modulesettings?.width) {
      case "66%":
        return 0.66;
      case "50%":
        return 0.5;
      case "33%":
        return 0.33;
      default:
        return 1;
    }
  }, [mod?.modulesettings?.width]);

  const handleOnChartCustomizationChange = useCallback(
    (newChartSettings: ChartSettings) => {
      onChartSettingsChanged(newChartSettings);

      if (!highchartOptions) return;

      const titleLength = newChartSettings?.title?.text?.length ?? 0;
      let titleFontSize = 28;
      let legendFontSize = 20;
      if (titleLength > 45 * widthFactor) {
        titleFontSize = 18;
        legendFontSize = 16;
      } else if (titleLength > 30 * widthFactor) {
        titleFontSize = 23;
        legendFontSize = 18;
      }

      const newDataset = Object.entries(newChartSettings?.datasets ?? {}).map(
        ([key, value]) => {
          const obj = find(
            highchartOptions?.series,
            (obj: any) => obj!.code === key,
          );
          const mergedObj = merge(obj, value);
          return mergedObj;
        },
      );

      const legendSettings = {
        ...highchartOptions?.legend,
        itemStyle: {
          ...highchartOptions?.legend?.itemStyle,
          fontSize: legendFontSize.toString(),
        },
      };
      const titleSettings = {
        ...highchartOptions?.title,
        text: newChartSettings?.title?.text ?? "",
        style: { fontSize: titleFontSize.toString() },
      };
      const sourceSettings = {
        ...highchartOptions?.credits,
        text:
          newChartSettings?.source?.length > 0
            ? `source: ${newChartSettings.source}`
            : (newChartSettings?.source ?? ""),
      };
      setHighchartOptions({
        ...highchartOptions,
        series: newDataset,
        legend: { ...legendSettings },
        title: { ...titleSettings },
        credits: { ...sourceSettings },
      });
    },
    [highchartOptions, widthFactor, onChartSettingsChanged],
  );

  return highchartOptions ? (
    <>
      <ChartEditor
        onChange={handleOnChartCustomizationChange}
        settings={chartSettings}
        roseObject={roseObject}
      />
      <MemoHighchartsReact options={highchartOptions} />
    </>
  ) : null;
}

type MemoHighchartsReactProps = {
  options: any;
};

const MemoHighchartsReact = React.memo<MemoHighchartsReactProps>(
  ({ options }: MemoHighchartsReactProps) => {
    const chartRef = useRef<HighchartsReact.RefObject>(null);
    const theme = useTheme();
    const prevThemeRef = useRef(theme);

    // Only update chart theme when theme actually changes
    useEffect(() => {
      const chart = chartRef.current?.chart;
      if (!chart || prevThemeRef.current === theme) return;

      prevThemeRef.current = theme;
      const themeSettings = setHighchartsTheme(theme);
      // Use redraw: false and call redraw once to batch updates
      chart.update(themeSettings, true, false, false);
    }, [theme]);

    return (
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
        containerProps={{
          style: { height: "500px", width: "100%", maxWidth: "100%" },
        }}
      />
    );
  },
  (prevProps, nextProps) => prevProps.options === nextProps.options,
);

MemoHighchartsReact.displayName = "MemoHighchartsReact";
